import React, { useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../supabase";

const MockInterviewModal = ({ isOpen, onClose, onStartPracticing, showConsentOnly = false }) => {
  const { user } = useAuth();
  const [paymentError, setPaymentError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showConsentStep, setShowConsentStep] = useState(showConsentOnly);
  const [consentInput, setConsentInput] = useState("");

  // Update showConsentStep when showConsentOnly prop changes
  React.useEffect(() => {
    setShowConsentStep(showConsentOnly);
    if (showConsentOnly) {
      setConsentInput(""); // Reset consent input when showing consent step directly
    }
  }, [showConsentOnly, isOpen]);

  const handleConsentContinue = async () => {
    const agreed = consentInput.trim().toLowerCase() === "i agree";
    if (!agreed || !user) return;

    setLoading(true);
    try {
      const now = new Date().toISOString();

      const { error: updateError } = await supabase
        .from('user_tokens')
        .update({
          mock_interview_consent: true,
          mock_interview_consent_at: now
        })
        .eq('user_id', user.id);

      if (updateError) {
        // Attempt upsert if the row doesn't exist yet
        const { error: upsertError } = await supabase
          .from('user_tokens')
          .upsert({
            user_id: user.id,
            tokens: 0,
            mock_interview_consent: true,
            mock_interview_consent_at: now
          }, { onConflict: 'user_id' });

        if (upsertError) {
          console.error('Error storing consent status:', upsertError);
          // We still allow proceeding even if analytics write fails
        }
      }
    } catch (err) {
      console.error('Unexpected error storing consent:', err);
      // Proceed regardless of telemetry failure
    } finally {
      setLoading(false);
      onClose();
      onStartPracticing();
    }
  };

  const handleStartPracticing = async () => {
    if (!user) {
      alert("Please login to continue");
      return;
    }

    const price = 75; // R75

    // Custom payment handler for mock interview
    setPaymentError(null);
    setLoading(true);
    try {
      const handler = window.PaystackPop.setup({
        key: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY,
        email: user.email,
        amount: price * 100, // Amount in kobo (multiply by 100)
        currency: 'ZAR',
        ref: `mock_interview_${user.id}_${Date.now()}`,
        metadata: {
          user_id: user.id,
          payment_type: 'mock_interview',
          custom_fields: [
            {
              display_name: "Mock Interview Payment",
              variable_name: "mock_interview",
              value: "Practice Interview Questions"
            }
          ]
        },
        callback: function(response) {
          // Paystack callback must be a regular function, not async
          // Handle async operations inside
          (async () => {
            try {
              // Store payment status in Supabase user_tokens table
              const { error: updateError } = await supabase
                .from('user_tokens')
                .update({
                  mock_interview_paid: true,
                  mock_interview_paid_at: new Date().toISOString()
                })
                .eq('user_id', user.id);

              if (updateError) {
                console.error("Error storing payment status:", updateError);
                console.error("Error details:", JSON.stringify(updateError, null, 2));
                
                // Try to upsert if update fails (in case user_tokens record doesn't exist)
                const { error: upsertError } = await supabase
                  .from('user_tokens')
                  .upsert({
                    user_id: user.id,
                    tokens: 0, // Default or existing tokens
                    mock_interview_paid: true,
                    mock_interview_paid_at: new Date().toISOString()
                  }, {
                    onConflict: 'user_id'
                  });

                if (upsertError) {
                  console.error("Error upserting payment status:", upsertError);
                  console.error("Upsert error details:", JSON.stringify(upsertError, null, 2));
                  setPaymentError(`Payment successful but database error: ${upsertError.message || upsertError.code || 'Unknown error'}. Please contact support.`);
                  setLoading(false);
                  return;
                }
              }

              // Show consent step before navigating to practice page
              setLoading(false);
              setShowConsentStep(true);
            } catch (err) {
              console.error("Error processing payment success:", err);
              setLoading(false);
              setPaymentError("Payment successful but there was an error. Please contact support.");
            }
          })();
        },
        onClose: () => {
          setPaymentError("Payment was cancelled");
          setLoading(false);
        }
      });

      handler.openIframe();
    } catch (err) {
      console.error("Error initializing payment:", err);
      setPaymentError(err.message || "Payment initialization failed. Please try again.");
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
      <div className="bg-white w-[92%] sm:w-[85%] lg:w-auto max-w-[700px] lg:max-w-5xl max-h-[90vh] overflow-y-auto flex flex-col relative shadow-2xl rounded-2xl lg:rounded-3xl">
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-2.5 lg:top-4 right-4 z-20 text-gray-900 hover:text-gray-700 transition-colors bg-white rounded-full p-2 lg:p-1 shadow-md"
          aria-label="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-6 w-6 lg:h-5 lg:w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Modal Content - Stacked vertically on mobile */}
        <div className="flex flex-col lg:flex-row h-full">
          {/* Image Section - hidden on mobile, visible on large screens */}
          <div className="hidden lg:block relative w-full lg:w-1/2 lg:h-auto lg:min-h-[500px] overflow-hidden flex-shrink-0 rounded-t-2xl lg:rounded-t-none bg-white">
            <img
              src="/Popup_mock_interview-updated.webp"
              alt="Mock Interview"
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback if image doesn't exist
                const fallback = e.target.nextElementSibling;
                if (fallback) fallback.style.display = 'flex';
                e.target.style.display = 'none';
              }}
            />
            {/* Fallback if image doesn't load */}
            <div className="hidden absolute inset-0 bg-gradient-to-br from-indigo-100 to-purple-100 flex items-center justify-center">
              <p className="text-gray-500 text-sm">Image: Popup_mock_interview-updated.webp</p>
            </div>
          </div>

          {/* Content Section - Below image on mobile, right side on desktop */}
          <div className="w-full lg:w-1/2 p-6 sm:p-8 lg:p-10 flex flex-col justify-start text-left lg:text-center flex-1">
            {!showConsentStep && (
              <>
                <div className="flex-1 mt-8 lg:mt-10">
                  {/* Title */}
                  <div className="flex flex-col items-start lg:items-center mb-6">
                    <div className="flex items-center gap-2 whitespace-nowrap">
                      <h2 className="text-lg sm:text-2xl lg:text-3xl font-bold text-gray-900 whitespace-nowrap">
                        Practice Interview Questions
                      </h2>
                      <span className="inline-flex items-center px-1 py-0.5 rounded border border-black bg-white text-black text-[9px] font-medium flex-shrink-0">
                        Beta
                      </span>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-gray-700 mb-8 leading-relaxed text-sm sm:text-base">
                    This AI-powered chat simulates clinical interviews, inspired by our question bank. Each chat includes{" "}
                    <strong className="font-semibold">6 interview questions per round</strong>, covering clinical scenarios and general questions.
                  </p>

                  {/* Pricing Section */}
                  <div className="mb-8 flex items-start gap-3 justify-center">
                    <p className="text-4xl sm:text-5xl font-bold text-gray-900">R75</p>
                    <div className="flex flex-col text-left">
                      <p className="text-base sm:text-lg font-normal lg:font-bold text-gray-900">for up to 40</p>
                      <p className="text-sm sm:text-base font-normal lg:font-bold text-gray-900">interview rounds</p>
                    </div>
                  </div>

                  {/* Benefits */}
                  <p className="text-gray-700 mb-2 sm:mb-6 lg:mb-0 text-sm sm:text-base leading-relaxed text-left lg:text-center">
                    Practise answering, receive guided prompts, and refine<span className="lg:hidden"> </span><br className="hidden lg:inline" />your responses before the real interview.
                  </p>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col gap-4 mt-2 lg:mt-2">
                  {paymentError && (
                    <div className="mb-2 p-3 bg-red-100 border border-red-400 text-red-700 rounded text-sm">
                      {paymentError}
                    </div>
                  )}
                  <button
                    onClick={handleStartPracticing}
                    disabled={loading}
                    className="w-full max-w-[280px] mx-auto bg-gray-900 hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold py-3.5 px-6 rounded-full transition-colors duration-200 shadow-md hover:shadow-lg"
                  >
                    {loading ? "Processing..." : "Start Practicing"}
                  </button>
                  <button
                    onClick={onClose}
                    className="w-full text-gray-600 hover:text-gray-800 underline py-2 transition-colors text-sm"
                  >
                    Skip interview practice for now
                  </button>
                </div>
              </>
            )}

            {showConsentStep && (
              <div className="flex flex-col gap-6 mt-6 lg:mt-16 text-left">
                <h3 className="text-2xl lg:text-3xl font-bold text-black">Thank you for your Purchase</h3>
                <p className="text-gray-700 text-sm sm:text-base leading-relaxed">
                  Before continuing, please note that this chat is intended for <strong className="font-semibold">educational purposes only</strong>. Please do not share any patient names, personal data, or confidential medical information during your session.
                </p>
                <div>
                  <label className="block text-gray-900 text-sm sm:text-base mb-2">
                    To continue, please type "I agree" in the field below. <span className="text-gray-900">*</span>
                  </label>
                  <input
                    type="text"
                    value={consentInput}
                    onChange={(e) => setConsentInput(e.target.value)}
                    placeholder='Type "I agree"'
                    className="w-full border border-gray-300 rounded-full px-4 py-3 text-sm sm:text-base text-gray-900 bg-white outline-none focus:ring-2 focus:ring-gray-900 focus:border-gray-900"
                    autoFocus
                  />
                  <p className="text-xs sm:text-sm text-gray-900 mt-2">You won't be able to proceed without agreeing to these terms.</p>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  <button
                    onClick={onClose}
                    className="bg-gray-200 hover:bg-gray-300 text-black font-medium px-6 py-3 rounded-full transition-colors text-sm sm:text-base"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConsentContinue}
                    disabled={consentInput.trim().toLowerCase() !== "i agree" || loading}
                    className="bg-black text-white hover:bg-gray-800 font-semibold px-6 py-3 rounded-full transition-colors text-sm sm:text-base ml-auto disabled:opacity-70 disabled:cursor-not-allowed disabled:hover:bg-black disabled:hover:text-white"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockInterviewModal;

